import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ClientEnv } from 'constants/Chat.constants';

import { projectInit } from 'actions';
import type { TurnMetadata } from 'api/resources/conversation/types';
import type { ChatMessage } from 'api/types';

export type AgentResponseV2 = {
  response: string;
  userInput: string;
  metadata?: TurnMetadata;
};

export type ChatStatus =
  | 'idle'
  | 'preparing'
  | 'starting'
  | 'ready'
  | 'sending'
  | 'waitingSlow'
  | 'ended'
  | 'error';

export interface EnvChatState {
  conversationId: string | null;
  messages: ChatMessage[];
  agentResponses: AgentResponseV2[];
  status: ChatStatus;
  requestId: number;
  messageDraft: string;
  endTurn: ChatMessage | null;
  variantName: string;
  channel: string;
  languageCode: string | null;
  asrLanguageCode: string | null;
  ttsLanguageCode: string | null;
}

export interface VoiceChatV2State {
  draft: EnvChatState;
  sandbox: EnvChatState;
  'pre-release': EnvChatState;
  live: EnvChatState;
  draftArtifactVersions: {
    artifact?: string;
    lambdaDeployment?: string;
  };
}

export const NO_VARIANT_NAME_VOICE = 'No variant';
const DEFAULT_CHANNEL = 'chat.polyai';

export const initialEnvVoiceChatState: EnvChatState = {
  conversationId: null,
  messages: [],
  agentResponses: [],
  status: 'idle',
  requestId: 0,
  messageDraft: '',
  endTurn: null,
  variantName: NO_VARIANT_NAME_VOICE,
  channel: DEFAULT_CHANNEL,
  languageCode: null,
  asrLanguageCode: null,
  ttsLanguageCode: null,
};

export const initialVoiceChatV2State: VoiceChatV2State = {
  draft: { ...initialEnvVoiceChatState },
  sandbox: { ...initialEnvVoiceChatState },
  'pre-release': { ...initialEnvVoiceChatState },
  live: { ...initialEnvVoiceChatState },
  draftArtifactVersions: {},
};

function resetConversation(envState: EnvChatState): EnvChatState {
  return {
    ...envState,
    conversationId: null,
    messages: [],
    agentResponses: [],
    status: 'idle',
    requestId: envState.requestId + 1,
    messageDraft: '',
    endTurn: null,
    asrLanguageCode: null,
    ttsLanguageCode: null,
  };
}

type EnvPayload = { clientEnv: ClientEnv };
type RequestIdPayload = { requestId: number } & EnvPayload;

const voiceChatV2Slice = createSlice({
  name: 'voiceChatV2',
  initialState: initialVoiceChatV2State,
  reducers: {
    voiceBeginPreparing: (
      state,
      { payload: { clientEnv } }: PayloadAction<EnvPayload>,
    ) => {
      if (state[clientEnv].status !== 'idle') return;
      state[clientEnv].status = 'preparing';
      state[clientEnv].requestId += 1;
    },

    voiceBeginStarting: (
      state,
      { payload: { clientEnv } }: PayloadAction<EnvPayload>,
    ) => {
      const env = state[clientEnv];
      if (env.status !== 'idle' && env.status !== 'preparing') return;
      if (env.status === 'idle') {
        env.requestId += 1;
      }
      env.status = 'starting';
    },

    voiceChatStarted: (
      state,
      {
        payload: {
          clientEnv,
          requestId,
          conversationId,
          agentMessage,
          agentResponse,
          asrLanguageCode,
          ttsLanguageCode,
        },
      }: PayloadAction<
        RequestIdPayload & {
          conversationId: string;
          agentMessage: ChatMessage;
          agentResponse: AgentResponseV2;
          asrLanguageCode?: string;
          ttsLanguageCode?: string;
        }
      >,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      if (state[clientEnv].status !== 'starting') return;
      const env = state[clientEnv];
      env.status = 'ready';
      env.conversationId = conversationId;
      if (asrLanguageCode) env.asrLanguageCode = asrLanguageCode;
      if (ttsLanguageCode) env.ttsLanguageCode = ttsLanguageCode;
      env.messages.push(agentMessage);
      env.agentResponses.push(agentResponse);
    },

    voiceBeginSending: (
      state,
      {
        payload: { clientEnv, message },
      }: PayloadAction<{ message: ChatMessage } & EnvPayload>,
    ) => {
      if (state[clientEnv].status !== 'ready') return;
      state[clientEnv].messages.push(message);
      state[clientEnv].messageDraft = '';
      state[clientEnv].status = 'sending';
      state[clientEnv].requestId += 1;
    },

    voiceSetWaitingSlow: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      if (state[clientEnv].status !== 'sending') return;
      state[clientEnv].status = 'waitingSlow';
    },

    voiceMessageSent: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      const { status } = state[clientEnv];
      if (status !== 'sending' && status !== 'waitingSlow') return;
      state[clientEnv].status = 'ready';
    },

    voiceChatEnded: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      state[clientEnv].status = 'ended';
    },

    voiceChatErrored: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      const { status } = state[clientEnv];
      if (
        status !== 'preparing' &&
        status !== 'starting' &&
        status !== 'sending' &&
        status !== 'waitingSlow'
      ) {
        return;
      }
      state[clientEnv].status = 'error';
    },

    voiceResponseReceived: (
      state,
      {
        payload: {
          clientEnv,
          requestId,
          conversationId,
          agentMessage,
          previousMessageMetadata,
          agentResponse,
          conversationEnded,
          asrLanguageCode,
          ttsLanguageCode,
        },
      }: PayloadAction<
        RequestIdPayload & {
          conversationId: string;
          agentMessage: ChatMessage;
          previousMessageMetadata: TurnMetadata;
          agentResponse: AgentResponseV2;
          conversationEnded?: boolean;
          asrLanguageCode?: string;
          ttsLanguageCode?: string;
        }
      >,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      if (state[clientEnv].conversationId !== conversationId) return;
      const { status } = state[clientEnv];
      if (status !== 'sending' && status !== 'waitingSlow') return;
      const env = state[clientEnv];
      if (asrLanguageCode) env.asrLanguageCode = asrLanguageCode;
      if (ttsLanguageCode) env.ttsLanguageCode = ttsLanguageCode;
      const previousMessage = env.messages.at(-1);
      if (previousMessage) {
        previousMessage.metadata = previousMessageMetadata;
      }
      env.messages.push(agentMessage);
      env.agentResponses.push(agentResponse);
      if (conversationEnded) env.status = 'ended';
    },

    setVoiceChatMessageDraft: (
      state,
      {
        payload: { clientEnv, message },
      }: PayloadAction<{ message: string } & EnvPayload>,
    ) => {
      state[clientEnv].messageDraft = message;
    },

    setVoiceEndTurns: (
      state,
      {
        payload: { clientEnv, message, metadata },
      }: PayloadAction<
        { message: string; metadata: TurnMetadata } & EnvPayload
      >,
    ) => {
      state[clientEnv].endTurn = {
        text: message,
        from: 'agent',
        metadata,
      };
    },

    setVoiceChatVariantName: (
      state,
      {
        payload: { clientEnv, variantName },
      }: PayloadAction<{ variantName: string } & EnvPayload>,
    ) => {
      if (state[clientEnv].variantName === variantName) return;
      state[clientEnv] = {
        ...resetConversation(state[clientEnv]),
        variantName,
      };
    },

    setVoiceChatLanguageCode: (
      state,
      {
        payload: { clientEnv, languageCode },
      }: PayloadAction<{ languageCode: string } & EnvPayload>,
    ) => {
      if (state[clientEnv].languageCode === languageCode) return;
      state[clientEnv] = {
        ...resetConversation(state[clientEnv]),
        languageCode,
      };
    },

    resetVoiceChatV2: (
      state,
      { payload }: PayloadAction<{ clientEnv?: ClientEnv } | undefined>,
    ) => {
      if (!payload?.clientEnv) {
        return { ...initialVoiceChatV2State };
      }
      state[payload.clientEnv] = resetConversation(state[payload.clientEnv]);
    },

    setVoiceDraftArtifactVersions: (
      state,
      {
        payload: { artifactVersion, lambdaDeployment },
      }: PayloadAction<{
        artifactVersion: string;
        lambdaDeployment?: string;
      }>,
    ) => {
      state.draftArtifactVersions = {
        artifact: artifactVersion,
        lambdaDeployment,
      };
    },

    resetVoiceDraftArtifactVersions: state => {
      state.draftArtifactVersions = {};
    },
  },
  extraReducers: builder => {
    builder.addCase(projectInit, () => ({ ...initialVoiceChatV2State }));
  },
});

export const {
  voiceBeginPreparing,
  voiceBeginStarting,
  voiceChatStarted,
  voiceBeginSending,
  voiceSetWaitingSlow,
  voiceMessageSent,
  voiceChatEnded,
  voiceChatErrored,
  voiceResponseReceived,
  setVoiceChatMessageDraft,
  setVoiceEndTurns,
  setVoiceChatVariantName,
  setVoiceChatLanguageCode,
  resetVoiceChatV2,
  setVoiceDraftArtifactVersions,
  resetVoiceDraftArtifactVersions,
} = voiceChatV2Slice.actions;

export default voiceChatV2Slice.reducer;
